import React, { useCallback, useMemo } from 'react'
import _get from 'lodash/get'
import styled from 'styled-components'
import getNextConfig from 'next/config'
import nookies from 'nookies'

import { getInstance, usePaymentMethods } from '@ntuctech/payment-sdk-react'

import NdsThemeProvider from '@ntuctech/devex-tangram/Theme/NdsThemeProvider'
import ButtonContained from '@ntuctech/devex-tangram/Button/ButtonContained'

import { AUTH_TOKEN_KEY, AccountConsumer } from './../../AccountProvider'
import { getEmail } from '../../../utils/Utils'
import { SPLIT_FEATURES } from '../../../utils/constants'
import { getConfigFor } from '../../../utils/configService'
import { getPaymentSplits } from '../../../lib/utils'

const ContinueButtonWrapper = styled.div`
  padding-top: 1.25rem;
  display: flex;
`

const StyledContinueButtonContainer = styled.div`
  margin-left: auto;

  @media (max-width: ${props =>
      `${parseInt(props.theme.SizeBreakpointsS, 10) - 1}px`}) {
    width: 100%;
  }

  button {
    min-width: auto !important;
  }
`

const {
  publicRuntimeConfig: { ENVIRONMENT, DOMAIN_URL, DYNAMIC_DOMAIN },
} = getNextConfig()

const isPreviewMode =
  ENVIRONMENT === 'UATPREVIEW' || ENVIRONMENT === 'PRODPREVIEW'
const isProdMode =
  ENVIRONMENT === 'PROD' ||
  DOMAIN_URL === 'https://preprod.fairprice.com.sg' ||
  ENVIRONMENT === 'PRODPREVIEW'

const APP_DOMAIN = isPreviewMode ? DYNAMIC_DOMAIN : DOMAIN_URL
const PaymentSDKInstance = getInstance()
PaymentSDKInstance.setConfig({
  env: isProdMode ? 'production' : 'preproduction',
  APP_DOMAIN,
  splits: getPaymentSplits(),
})

function CardPaymentSDKComp({
  onSelect,
  accountData = {},
  isMemberShipFlowEnabled,
  isGooglePayAvailable,
  isApplePayAvailable,
  defaultCard,
  cardServiceDown,
  googleServiceDown,
  appleServiceDown,
}) {
  const { PaymentMethodsWrapper, selectNextPaymentMethod } = usePaymentMethods()
  const [selectedCard, setSelectedCard] = React.useState(defaultCard)
  const [selectedMethod, setSelectedMethod] = React.useState(null)
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const [isReady, setIsReady] = React.useState(false)
  const SubmitRef = React.useRef()
  const isPaymentSelected =
    selectedCard || (selectedMethod && selectedMethod !== 'card')

  const handleGetAuthToken = useCallback(() => {
    if (typeof window !== 'undefined') {
      return nookies.get({})[AUTH_TOKEN_KEY]
    }

    return ''
  }, [])

  const handleOnSubmitAddPaymentRef = submitFn => {
    SubmitRef.current = submitFn
  }

  const handleSubmitForm = useCallback(() => {
    if (!selectedCard) {
      SubmitRef.current()
    } else {
      onSelect(selectedCard)
    }
  }, [selectedCard, onSelect])

  const handleOnSubmitStart = useCallback(() => {
    setIsSubmitting(true)
  }, [])

  const handleOnSubmitEnd = useCallback(
    card => {
      setIsSubmitting(false)
      setSelectedCard(card)
      if (card) {
        // add third params indicate new card
        onSelect(card, null, true)
      }
    },
    [onSelect]
  )

  const handleOnReady = useCallback(isReadyValue => {
    setIsReady(isReadyValue)
  }, [])

  const handleOnChange = useCallback((card, method) => {
    setSelectedCard(card)
    setSelectedMethod(method)
  }, [])

  const handleSubmitButtonAction = useCallback(
    e => {
      if (isSubmitting) {
        e.preventDefault()
        return
      }

      if (isPaymentSelected) {
        cardServiceDown
          ? selectNextPaymentMethod((nextPaymentMethodType, nextCard) => {
              onSelect && onSelect(nextCard, nextPaymentMethodType)
            })
          : onSelect && onSelect(selectedCard, selectedMethod)
        return
      }

      handleSubmitForm()
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      isSubmitting,
      isPaymentSelected,
      handleSubmitForm,
      onSelect,
      selectedCard,
      selectedMethod,
    ]
  )

  // split flag for apple pay and google pay
  const { isEnabled: isApplePayGooglePayAvailable } = getConfigFor(
    SPLIT_FEATURES.PAYMENT_SDK_APPLE_GOOGLE_PAY_ENABLED
  )

  const availablePaymentMethods = useMemo(() => {
    const defaultPaymentMethod = ['card']
    const showGooglePay = false
    if (isMemberShipFlowEnabled || !isApplePayGooglePayAvailable) {
      return defaultPaymentMethod
    }
    if (isGooglePayAvailable && showGooglePay) {
      defaultPaymentMethod.push('google-pay')
    }
    if (isApplePayAvailable) {
      defaultPaymentMethod.push('apple-pay')
    }
    return defaultPaymentMethod
  }, [
    isApplePayGooglePayAvailable,
    isMemberShipFlowEnabled,
    isGooglePayAvailable,
    isApplePayAvailable,
  ])

  const paymentGatewayDownConfig = React.useMemo(() => {
    const isPaymentGatewayDown = getConfigFor(
      SPLIT_FEATURES.PAYMENT_GATEWAY_DOWN
    )
    return isPaymentGatewayDown?.configuration
    // intended unrelated deps, because the pgstatus only deps was this config
    // but the payment availability may changed asynchronously
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availablePaymentMethods])

  const pgStatusAllDown =
    isApplePayAvailable && isGooglePayAvailable
      ? googleServiceDown && appleServiceDown && cardServiceDown
      : googleServiceDown && cardServiceDown

  const customerEmail = getEmail(accountData)
  const customerName = _get(accountData, 'name') || ''
  const customerId = _get(accountData, 'id') || ''

  let buttonText = 'Save and continue'
  if (isPaymentSelected) {
    buttonText = 'Continue'
  }
  if (isSubmitting) {
    buttonText = 'Processing...'
  }

  return (
    <div data-testid="checkoutPaymentMethods">
      <div data-testid="paymentSDKMethods">
        <NdsThemeProvider>
          <PaymentMethodsWrapper
            paymentMethods={availablePaymentMethods}
            getAuthToken={handleGetAuthToken}
            mode="select-add"
            flowOTPVerify="implicit"
            selectedCardId={selectedCard?.id}
            selectedPaymentMethod={selectedMethod}
            onSubmitAddPaymentRef={handleOnSubmitAddPaymentRef}
            onSubmitStart={handleOnSubmitStart}
            onSubmitEnd={handleOnSubmitEnd}
            onReady={handleOnReady}
            onChange={handleOnChange}
            customer={{
              id: customerId,
              email: customerEmail,
            }}
            customerName={customerName}
            paymentGatewayDownConfig={paymentGatewayDownConfig}
          />

          {isReady && (
            <ContinueButtonWrapper>
              <StyledContinueButtonContainer>
                <ButtonContained
                  disabled={pgStatusAllDown || isSubmitting}
                  compact
                  size="m"
                  fullWidth
                  className="ndsHidden-s-up"
                  data-testid="checkout-payment-continue-button"
                  onClick={handleSubmitButtonAction}
                >
                  {buttonText}
                </ButtonContained>

                <ButtonContained
                  disabled={pgStatusAllDown || isSubmitting}
                  compact
                  size="m"
                  data-testid="checkout-payment-continue-button"
                  className="ndsHidden-xs"
                  onClick={handleSubmitButtonAction}
                >
                  {buttonText}
                </ButtonContained>
              </StyledContinueButtonContainer>
            </ContinueButtonWrapper>
          )}
        </NdsThemeProvider>
      </div>
    </div>
  )
}

export default function CardPayment(props) {
  return (
    <AccountConsumer>
      {accProp => {
        return <CardPaymentSDKComp {...props} {...accProp} />
      }}
    </AccountConsumer>
  )
}
